<template>
  <div class="question-table">
    <!-- 筛选区 -->
    <div class="filter-area">
      <el-input
        v-model="keyword"
        placeholder="按题干模糊查询"
        clearable
        size="small"
        style="width: 240px; margin-right: 12px;"
        @clear="loadData"
        @keyup.enter.native="loadData"
      />
      <el-checkbox v-model="onlyAbnormal" @change="loadData">只看异常题目</el-checkbox>
      <el-button type="primary" size="small" style="margin-left: 12px;" @click="handleSearch">查询</el-button>
      <el-button size="small" @click="handleExportExcel">导出Excel</el-button>
    </div>

    <!-- 表格 -->
    <el-table
      ref="questionTable"
      :data="tableData"
      border
      size="small"
      v-loading="loading"
      style="width: 100%"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="questionNo" label="题号" width="60" align="center" />
      <el-table-column prop="questionStemSummary" label="题干摘要" min-width="130" show-overflow-tooltip>
        <template slot-scope="scope">
          <el-tooltip :content="scope.row.questionStem" placement="top" :disabled="!scope.row.questionStem">
            <span>{{ scope.row.questionStemSummary || '—' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="questionType" label="题型" width="180" align="center" />
      <el-table-column prop="knowledgePoint" label="知识点" width="120" align="center" show-overflow-tooltip />
      <el-table-column prop="fullScore" label="满分" width="90" align="center" />
      <el-table-column prop="estimatedDifficulty" label="预估难度" width="90" align="center" />
      <el-table-column prop="measuredDifficulty" label="实测难度" width="90" align="center">
        <template slot-scope="scope">
          <span :class="{ 'text-danger': isInconsistent(scope.row) }">{{ scope.row.measuredDifficulty }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="correctRate" label="正确率" width="90" align="center" sortable="custom">
        <template slot-scope="scope">
          <span :style="{ color: getCorrectRateColor(scope.row.correctRate) }">
            {{ formatPercent(scope.row.correctRate) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="discrimination" label="区分度" width="90" align="center">
        <template slot-scope="scope">
          <span :class="{ 'text-danger': isDiscriminationAbnormal(scope.row) }">
            {{ scope.row.discrimination }}
          </span>
        </template>
      </el-table-column>
      <!-- 随机卷特有列 -->
      <el-table-column v-if="paperForm === 2" prop="extractCount" label="被抽次数" width="120" align="center" sortable="custom" />
      <el-table-column v-if="paperForm === 2" prop="exposureRate" label="曝光率" width="90" align="center">
        <template slot-scope="scope">
          {{ formatPercent(scope.row.exposureRate) }}
        </template>
      </el-table-column>
      <el-table-column prop="isAbnormal" label="状态" width="80" align="center" >
        <template slot-scope="scope">
          <el-tooltip
            v-if="scope.row.isAbnormal === 1"
            :content="scope.row.abnormalType || '异常'"
            placement="top"
            :disabled="!scope.row.abnormalType"
          >
            <span class="text-danger">异常</span>
          </el-tooltip>
          <span v-else>—</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-area">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import { getQuestionListAPI, exportExcelAPI } from '@/http/modules/examSet/paperAnalysis'
import { downloadAnalysisExcel } from '@/http/modules/examSet/paperAnalysisDownload'

export default {
  name: 'QuestionTable',
  props: {
    resultId: { type: String, required: true },
    paperForm: { type: Number, required: true }
  },
  data() {
    return {
      loading: false,
      keyword: '',
      onlyAbnormal: false,
      tableData: [],
      pageNo: 1,
      pageSize: 10,
      total: 0,
      sortField: '',
      sortOrder: ''
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          resultId: this.resultId,
          keyword: this.keyword,
          onlyAbnormal: this.onlyAbnormal ? 1 : 0,
          page: this.pageNo,
          size: this.pageSize
        }
        if (this.sortField && this.sortOrder) {
          params.sortField = this.sortField
          params.sortOrder = this.sortOrder
        }
        const res = await getQuestionListAPI(params)
        if (res.code === 1 && res.data) {
          this.tableData = res.data.records || []
          this.total = res.data.total || 0
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.sortField = ''
      this.sortOrder = ''
      if (this.$refs.questionTable) {
        this.$refs.questionTable.clearSort()
      }
      this.pageNo = 1
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.sortField = order ? prop : ''
      this.sortOrder = order || ''
      this.pageNo = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.pageNo = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pageNo = val
      this.loadData()
    },
    formatPercent(val) {
      if (val == null) return '—'
      return (val * 100).toFixed(1) + '%'
    },
    getCorrectRateColor(rate) {
      // if (rate == null) return ''
      // if (rate >= 0.8) return '#67C23A'
      // if (rate >= 0.5) return '#E6A23C'
      return '#606266'
    },
    isInconsistent(row) {
      return row.measuredDifficulty && row.estimatedDifficulty && row.measuredDifficulty !== row.estimatedDifficulty
    },
    isDiscriminationAbnormal(row) {
      return row.isAbnormal === 1 && row.abnormalType && row.abnormalType.includes('区分度')
    },
    handleExportExcel() {
      //  实现导出Excel
      downloadAnalysisExcel(this.resultId, this.courseName) 
    }
  }
}
</script>

<style lang="less" scoped>
.question-table { margin-top: 20px; }
.filter-area { margin-bottom: 12px; display: flex; align-items: center; }
.pagination-area { 
  margin-top: 12px; 
  margin-right: 50px;
  text-align: right; 
  /deep/ .el-input__inner{ width: 110px; }
}
.text-danger { color: #F56C6C; font-weight: bold; }
</style>
